import React, { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTenant } from '../context/TenantContext'
import { useAuth } from '../context/AuthContext'
import { useTheme } from '../context/ThemeContext'
import moduleConfig from '../config/moduleConfig'
import {
  HomeIcon,
  EllipsisHorizontalIcon,
  XMarkIcon,
  Cog6ToothIcon,
  UserCircleIcon,
  BellIcon,
  SunIcon,
  MoonIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  ArrowRightOnRectangleIcon,
  QuestionMarkCircleIcon,
  BuildingOffice2Icon
} from '@heroicons/react/24/outline'
import DepartmentSwitcher from './DepartmentSwitcher'
import { isNative } from '../services/native'
import { activeDepartment } from '../services/departments'

// Tab icon with a pill behind it when active: the current tab reads at a
// glance instead of relying on colour plus a 4px dot.
function TabIcon({ Icon, active }) {
  return (
    <span className={`flex items-center justify-center w-14 h-7 rounded-full transition-colors ${active ? 'bg-amber-500/15' : ''}`}>
      <Icon className={`w-6 h-6 ${active ? 'text-amber-500' : 'text-slate-400'}`} />
    </span>
  )
}

function BottomNav({ onShowFeedback }) {
  const { orderedModules } = useTenant()
  const { user, logout, isPinDevice } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const location = useLocation()
  const navigate = useNavigate()
  const [moreOpen, setMoreOpen] = useState(false)
  const [switcherOpen, setSwitcherOpen] = useState(false)
  // A station device belongs to one department
  const canSwitch = isNative() && !isPinDevice

  const enabledModules = orderedModules
    .filter(m => moduleConfig[m.module_name])
    .map(m => ({
      ...moduleConfig[m.module_name],
      moduleName: m.module_name
    }))

  const tabModules = enabledModules.slice(0, 3)
  const moreModules = enabledModules.slice(3)
  const hasMore = moreModules.length > 0 || true // always show More for settings/profile/etc

  const isPathActive = (path) => {
    if (path === '/') return location.pathname === '/'
    return location.pathname === path || location.pathname.startsWith(path + '/')
  }

  const handleNav = (path) => {
    if (isPathActive(path)) {
      window.location.href = path
    } else {
      navigate(path)
    }
  }

  const handleMoreNav = (path) => {
    setMoreOpen(false)
    if (isPathActive(path)) {
      window.location.href = path
    } else {
      navigate(path)
    }
  }

  const handleLogout = async () => {
    setMoreOpen(false)
    await logout()
  }

  const dashboardActive = location.pathname === '/'

  return (
    <>
      {/* Bottom tab bar */}
      <div className="fixed bottom-0 left-0 right-0 z-40 lg:hidden glass border-t border-slate-700 safe-bottom">
        <div className="flex items-center justify-around h-16 px-1">
          {/* Dashboard tab */}
          <button
            onClick={() => handleNav('/')}
            className="flex flex-col items-center justify-center flex-1 h-full gap-0.5"
          >
            <TabIcon Icon={HomeIcon} active={dashboardActive} />
            <span className={`text-[10px] font-medium ${dashboardActive ? 'text-amber-500' : 'text-slate-400'}`}>
              Dashboard
            </span>
          </button>

          {/* Module tabs */}
          {tabModules.map(module => {
            const active = isPathActive(module.path)
            return (
              <button
                key={module.moduleName}
                onClick={() => handleNav(module.path)}
                className="flex flex-col items-center justify-center flex-1 h-full gap-0.5"
              >
                <TabIcon Icon={module.Icon} active={active} />
                <span className={`text-[10px] font-medium truncate max-w-[64px] ${active ? 'text-amber-500' : 'text-slate-400'}`}>
                  {module.name}
                </span>
              </button>
            )
          })}

          {/* More tab */}
          <button
            onClick={() => setMoreOpen(true)}
            className="flex flex-col items-center justify-center flex-1 h-full gap-0.5"
          >
            <TabIcon Icon={EllipsisHorizontalIcon} active={moreOpen} />
            <span className={`text-[10px] font-medium ${moreOpen ? 'text-amber-500' : 'text-slate-400'}`}>
              More
            </span>
          </button>
        </div>
      </div>

      {/* More overlay */}
      {moreOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" onClick={() => setMoreOpen(false)}>
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />

          {/* Sheet */}
          <div
            className="absolute bottom-0 left-0 right-0 glass-surface rounded-t-3xl border-t border-slate-700 max-h-[85vh] overflow-y-auto safe-bottom"
            onClick={e => e.stopPropagation()}
          >
            {/* Handle bar */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 rounded-full bg-slate-600" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-5 pb-3">
              <h3 className="text-lg font-semibold text-white">More</h3>
              <button onClick={() => setMoreOpen(false)} className="p-1.5 text-slate-400 hover:text-white">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Remaining modules */}
            {moreModules.length > 0 && (
              <div className="px-3 pb-2">
                <p className="px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Modules</p>
                {moreModules.map(module => {
                  const active = isPathActive(module.path)
                  return (
                    <button
                      key={module.moduleName}
                      onClick={() => handleMoreNav(module.path)}
                      className={`flex items-center gap-3 w-full px-3 py-3 rounded-lg text-left transition-colors ${
                        active ? 'bg-amber-500/20 text-amber-500' : 'text-slate-300 hover:bg-slate-700/50'
                      }`}
                    >
                      <module.Icon className="w-5 h-5 flex-shrink-0" />
                      <span className="font-medium">{module.name}</span>
                    </button>
                  )
                })}
              </div>
            )}

            {/* Settings & Account */}
            <div className="border-t border-slate-700 mx-3 my-1" />
            <div className="px-3 pb-2">
              <p className="px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 mt-2">Account</p>

              <button
                onClick={() => handleMoreNav('/settings')}
                className={`flex items-center gap-3 w-full px-3 py-3 rounded-lg text-left transition-colors ${
                  isPathActive('/settings') && !isPathActive('/settings/profile') && !isPathActive('/settings/notifications')
                    ? 'bg-amber-500/20 text-amber-500'
                    : 'text-slate-300 hover:bg-slate-700/50'
                }`}
              >
                <Cog6ToothIcon className="w-5 h-5 flex-shrink-0" />
                <span className="font-medium">Settings</span>
              </button>

              <button
                onClick={() => handleMoreNav('/settings/profile')}
                className={`flex items-center gap-3 w-full px-3 py-3 rounded-lg text-left transition-colors ${
                  isPathActive('/settings/profile')
                    ? 'bg-amber-500/20 text-amber-500'
                    : 'text-slate-300 hover:bg-slate-700/50'
                }`}
              >
                <UserCircleIcon className="w-5 h-5 flex-shrink-0" />
                <span className="font-medium">Profile Settings</span>
              </button>

              <button
                onClick={() => handleMoreNav('/settings/notifications')}
                className={`flex items-center gap-3 w-full px-3 py-3 rounded-lg text-left transition-colors ${
                  isPathActive('/settings/notifications')
                    ? 'bg-amber-500/20 text-amber-500'
                    : 'text-slate-300 hover:bg-slate-700/50'
                }`}
              >
                <BellIcon className="w-5 h-5 flex-shrink-0" />
                <span className="font-medium">Notifications</span>
              </button>

              {canSwitch && (
                <button
                  onClick={() => { setMoreOpen(false); setSwitcherOpen(true) }}
                  className="flex items-center gap-3 w-full px-3 py-3 rounded-lg text-left text-slate-300 hover:bg-slate-700/50 transition-colors"
                >
                  <BuildingOffice2Icon className="w-5 h-5 flex-shrink-0" />
                  <span className="flex-1 min-w-0">
                    <span className="block font-medium">Switch Department</span>
                    {activeDepartment()?.name && <span className="block text-xs text-slate-500 truncate">{activeDepartment().name}</span>}
                  </span>
                </button>
              )}
            </div>

            {/* Theme & Actions */}
            <div className="border-t border-slate-700 mx-3 my-1" />
            <div className="px-3 pb-2">
              <button
                onClick={() => { toggleTheme(); setMoreOpen(false) }}
                className="flex items-center gap-3 w-full px-3 py-3 rounded-lg text-left text-slate-300 hover:bg-slate-700/50 transition-colors"
              >
                {theme === 'dark' ? <SunIcon className="w-5 h-5" /> : <MoonIcon className="w-5 h-5" />}
                <span className="font-medium">{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
              </button>

              <button
                onClick={() => handleMoreNav('/help')}
                className={`flex items-center gap-3 w-full px-3 py-3 rounded-lg text-left transition-colors ${
                  isPathActive('/help')
                    ? 'bg-amber-500/20 text-amber-500'
                    : 'text-slate-300 hover:bg-slate-700/50'
                }`}
              >
                <QuestionMarkCircleIcon className="w-5 h-5" />
                <span className="font-medium">Help</span>
              </button>

              <button
                onClick={() => { setMoreOpen(false); onShowFeedback() }}
                className="flex items-center gap-3 w-full px-3 py-3 rounded-lg text-left text-slate-300 hover:bg-slate-700/50 transition-colors"
              >
                <ChatBubbleOvalLeftEllipsisIcon className="w-5 h-5" />
                <span className="font-medium">Provide Feedback</span>
              </button>
            </div>

            {/* Sign Out */}
            <div className="border-t border-slate-700 mx-3 my-1" />
            <div className="px-3 pb-6">
              <button
                onClick={handleLogout}
                className="flex items-center gap-3 w-full px-3 py-3 rounded-lg text-left text-red-400 hover:bg-slate-700/50 transition-colors"
              >
                <ArrowRightOnRectangleIcon className="w-5 h-5" />
                <span className="font-medium">Sign Out</span>
              </button>
            </div>
          </div>
        </div>
      )}
      {switcherOpen && <DepartmentSwitcher onClose={() => setSwitcherOpen(false)} />}
    </>
  )
}

export default BottomNav
